import { fr } from './fr';

type Dictionary = typeof fr;

/** Chemins pointés vers les feuilles (chaînes) d'un objet imbriqué : 'auth.login.title'. */
type DotPath<T> = {
  [K in keyof T & string]: T[K] extends string
    ? K
    : T[K] extends Record<string, unknown>
      ? `${K}.${DotPath<T[K]>}`
      : never;
}[keyof T & string];

export type MessageKey = DotPath<Dictionary>;

type StripPluralSuffix<K> = K extends `${infer Base}_one` ? Base : never;

/** Clés de pluriel : toute clé `x_one` doit avoir son pendant `x_other`. */
export type PluralKey = StripPluralSuffix<MessageKey>;

export type MessageParams = Record<string, string | number>;

const pluralRules = new Intl.PluralRules('fr');

function lookup(key: string): string | undefined {
  let node: unknown = fr;
  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null || !(segment in node)) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

/** Garde de type pour les clés construites dynamiquement (`errors.${code}`). */
export function isMessageKey(key: string): key is MessageKey {
  return lookup(key) !== undefined;
}

export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/** Traduit une clé typée. Une clé inconnue est une erreur de type ; à l'exécution, une erreur. */
export function t(key: MessageKey, params?: MessageParams): string {
  const template = lookup(key);
  if (template === undefined) throw new Error(`Missing i18n key: ${key}`);
  return interpolate(template, params);
}

/** Pluriel : choisit `${key}_one` ou `${key}_other` selon `Intl.PluralRules('fr')`. */
export function tp(key: PluralKey, count: number, params?: MessageParams): string {
  const suffix = pluralRules.select(count) === 'one' ? '_one' : '_other';
  const fullKey: MessageKey = `${key}${suffix}`;
  return t(fullKey, { count, ...params });
}

export function useT(): typeof t {
  return t;
}
